import Utils from "context/utils";
import { SESSION_STATUS, isSessionOpen, isSessionTransitioning } from "./sessionStatus";

// Page size for session-object listing (matches the backend ceiling/default; fewest round-trips).
const OBJECTS_PAGE_LIMIT = 1000;
const OBJECTS_LIST_MAX_PAGES = 1000;

// Session lifecycle events that carry a new status for the *current* session directly (the
// backend emits these with no payload -- the event type itself is the signal). session_deleted
// is handled separately below: the session no longer exists, so there's no status to set --
// it's cleared instead. session_deleting is a standalone signal and may arrive with no
// preceding session_closed; reopen_session emits no event (the acting client already has the
// new status from its own response), so there's no "reopened" entry here.
const SESSION_LIFECYCLE_EVENT_STATUS = {
  session_closing: SESSION_STATUS.CLOSING,
  session_closed: SESSION_STATUS.CLOSED,
  session_deleting: SESSION_STATUS.DELETING
};

// Extracts the message for a failed update_session call. revisionsIdDataCommandPost rejects with
// the RAW axios error, so the backend's explanation (e.g. "session_not_open") lives only at
// error.response.data.message, not the generic ".message" the transport sets. Mirrors
// debugger/session/index.js#lifecycleErrorMessage, which the debugger's own Edit affordance uses
// for the same reason.
function updateSessionErrorMessage(error) {
  return error?.response?.data?.message || error?.message || "Unknown error";
}

// A collision is HTTP 400 with error="alias_in_use" in the body. The message is checked too so a
// body that carries only `message` still lands on the field rather than reading as a generic
// failure. Mirrors debugger/session/index.js#isAliasInUse.
function isUpdateSessionAliasInUse(error) {
  const data = error?.response?.data;
  if (error?.response?.status !== 400) {
    return false;
  }
  return data?.error === "alias_in_use" || /alias_in_use/.test(data?.message || "");
}

export default class SessionManager {
  constructor(app, revision, updateProgress, onUpdate) {
    this.app = app;
    this.revision = revision;
    this.updateProgress = updateProgress;
    this.listeners = new Set();
    if (onUpdate) {
      this.listeners.add(onUpdate);
    }

    // Session state
    this.sessions = null;
    this.sessionId = null;
    this.session = null;
    this.sessionObjects = null;
    this.sessionData = {};
    this.sessionEvents = null;
    this.sessionEventCreatedAt = null;
    this.loadingSessionObjects = new Set();
    this.threadEventListeners = new Map();   // alias -> Set<callback>
    this.graphEventListeners = new Map();    // alias -> Set<callback>
    
    // Sync state
    this.sessionSync = false;
    this.sessionSyncCounter = 0;
    this.lastSessionId = null;

    // Subscriptions
    this.subscriptions = {};
    
    // Timer for auto-refresh
    this.mounted = true;
    this.autoRefreshTimer = null;
  }

  destroy() {
    this.mounted = false;
    if (this.autoRefreshTimer) {
      clearTimeout(this.autoRefreshTimer);
    }
  }

  /**
   * Subscriptions
   */

  subscribe(alias, callback) {
    if (!this.subscriptions[alias]) {
      this.subscriptions[alias] = [];
    }
    this.subscriptions[alias].push(callback);
    
    // If data is already available, notify immediately
    if (this.sessionData[alias]) {
      callback(this.sessionData[alias]);
    }

    return () => {
      this.unsubscribe(alias, callback);
    }
  }

  unsubscribe(alias, callback) {
    if (!this.subscriptions[alias]) {
      return;
    }
    this.subscriptions[alias] = this.subscriptions[alias].filter(cb => cb !== callback);
  }

  notify(alias, value) {
    if (!this.subscriptions[alias]) {
      return;
    }
    for (const callback of this.subscriptions[alias]) {
      try {
        callback(value);
      } catch (e) {
        console.error(`Error notifying subscription for ${alias}:`, e);
      }
    }
  }

  addListener(callback) {
    this.listeners.add(callback);
  }

  removeListener(callback) {
    this.listeners.delete(callback);
  }

  /**
   * Fires every registered listener. `event` is optional: most call sites mean nothing more than
   * "something changed, re-render", and their listeners take no arguments. Lifecycle transitions
   * that a listener may need to act on specifically (currently only `{ type: "session_deleted" }`)
   * pass a descriptor through, which argument-less listeners simply ignore.
   */
  notifyListeners(event) {
    for (const listener of this.listeners) {
      try {
        listener(event);
      } catch (e) {
        console.error("Error notifying listener:", e);
      }
    }
  }

  /**
   * Thread event listeners (alias-scoped, fired on session_thread_item_posted events)
   */

  addThreadEventListener(alias, callback) {
    if (!this.threadEventListeners.has(alias)) {
      this.threadEventListeners.set(alias, new Set());
    }
    this.threadEventListeners.get(alias).add(callback);
  }

  removeThreadEventListener(alias, callback) {
    const set = this.threadEventListeners.get(alias);
    if (!set) return;
    set.delete(callback);
    if (set.size === 0) {
      this.threadEventListeners.delete(alias);
    }
  }

  notifyThreadEventListeners(alias, payload) {
    const set = this.threadEventListeners.get(alias);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(payload);
      } catch (e) {
        console.error(`Error notifying thread listener for ${alias}:`, e);
      }
    }
  }

  /**
   * Graph event listeners (alias-scoped, fired on session_graph_changed events)
   */

  addGraphEventListener(alias, callback) {
    if (!this.graphEventListeners.has(alias)) {
      this.graphEventListeners.set(alias, new Set());
    }
    this.graphEventListeners.get(alias).add(callback);
  }

  removeGraphEventListener(alias, callback) {
    const set = this.graphEventListeners.get(alias);
    if (!set) return;
    set.delete(callback);
    if (set.size === 0) {
      this.graphEventListeners.delete(alias);
    }
  }

  notifyGraphEventListeners(alias, payload) {
    const set = this.graphEventListeners.get(alias);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(payload);
      } catch (e) {
        console.error(`Error notifying graph listener for ${alias}:`, e);
      }
    }
  }

  /**
   * Data Access
   */

  getSessionData(alias) {
    return this.sessionData[alias];
  }

  /**
   * Whether the current session accepts mutations. Thin wrapper around
   * sessionStatus.isSessionOpen(session) so UI consumers (playground/index.js's
   * shellInterface) don't need to reach into `this.session` directly. The
   * session-events poller below (autoRefreshSessionEvents) checks
   * `isSessionOpen(this.session) || isSessionTransitioning(this.session)` inline
   * rather than calling this: the poller must keep running through "closing"/
   * "deleting" to observe the terminal lifecycle event, whereas mutations must
   * stay gated to strictly "open".
   */
  isSessionOpen() {
    return isSessionOpen(this.session);
  }

  /**
   * Actions
   */

  clearSessionState(session) {
    // Treat an empty/missing session as "no session selected" so the details
    // pane doesn't get stuck rendering Session Objects with sessionObjects=null
    // (which surfaces as a permanent "Loading session objects..." alert).
    const hasSession = session && session["id"];
    this.sessionId = hasSession ? session["id"] : null;
    this.session = hasSession ? session : null;
    this.sessionObjects = null;
    this.sessionData = {};
    this.sessionEvents = null;
    this.sessionEventCreatedAt = null;
    this.loadingSessionObjects.clear();
    // Drop stale thread/graph listeners; subscribers re-subscribe in componentDidUpdate.
    this.threadEventListeners = new Map();
    this.graphEventListeners = new Map();
  }

  loadSessions(more) {
    this.updateProgress(true, more ? "Loading more sessions..." : "Loading sessions...", false, null);

    const body = {
      engine: "workflow_blobhub",
      command: "list_sessions"
    };

    if (more) {
      body["start_session_id"] = this.lastSessionId;
    }

    return this.app.api.revisionsIdDataQueryPost(this.revision.id, body)
      .then(response => {
        this.updateProgress(false, null, false, null);
        let sessions = response.data.sessions;
        this.lastSessionId = response.data.last_session_id;

        if (more) {
          this.sessions = (this.sessions || []).concat(sessions);
        } else {
            this.sessions = sessions;
        }

        // Re-sync the currently-selected session (not just the list): the details pane reads
        // this.session, so without this it keeps showing a stale status after a lifecycle
        // transition until an unrelated poller update happens to touch it. Only update when the
        // freshly-loaded data actually includes it -- a paginated `more` load may not.
        if (this.session) {
          const refreshedSession = this.sessions.find(s => s["id"] === this.session["id"]);
          if (refreshedSession) {
            this.updateCurrentSession(refreshedSession);
          }
        }

        if (this.onUpdate) this.onUpdate();
        this.notifyListeners();

        return this.sessions;
      }, error => {
        this.updateProgress(false, null, true, "Failed to load sessions: " + error.message);
      });
  }

  loadSessionContent() {
    const requestedSessionId = this.sessionId;
    const revisionId = this.revision.id;

    const fetchAllObjects = async () => {
      const collected = [];
      let cursor = null;
      // Defensive page bound so a never-clearing cursor can't spin forever.
      for ( let page = 0; page < OBJECTS_LIST_MAX_PAGES; page++ ) {
        const body = {
          "engine": "workflow_blobhub",
          "command": "list_session_objects",
          "session_id": requestedSessionId,
          "limit": OBJECTS_PAGE_LIMIT
        };
        if (cursor) body.cursor = cursor;

        const response = await this.app.api.revisionsIdDataQueryPost(revisionId, body);
        // Bail if the active session changed while paging.
        if (this.sessionId !== requestedSessionId) return null;

        const pageObjects = (response.data["objects"] || []).map(obj => {
          if (obj.updated_at) {
            obj.updatedAt = obj.updated_at;
            delete obj.updated_at;
          }
          return obj;
        });
        collected.push(...pageObjects);
        cursor = response.data["cursor"] || null;
        if (!cursor) break;
      }
      return collected;
    };

    return fetchAllObjects().then(objects => {
      if (objects === null || this.sessionId !== requestedSessionId) return;

      this.sessionObjects = objects;
      for (const object of this.sessionObjects) {
        this.loadSessionObject(object["alias"]);
      }
      this.notifyListeners();
    }, error => {
      console.error("Failed to load session content: " + error.message);
    });
  }

  loadSessionObject(alias) {
    const body = {
      "engine": "workflow_blobhub",
      "command": "download_session_object",
      "session_id": this.sessionId,
      "alias": alias
    };
    
    this.loadingSessionObjects.add(alias);
    this.notifyListeners();

    const requestedSessionId = this.sessionId;

    return this.app.api.revisionsIdDataQueryPost(this.revision.id, body)
      .then(response => {
        if (this.sessionId !== requestedSessionId) return;

        const object = response.data["object"];
        const value = object["value"];
        this.sessionData[alias] = value;
        
        // Update session object metadata
        if (this.sessionObjects) {
          const sessionObject = this.sessionObjects.find(obj => obj.alias === alias);
          if (sessionObject) {
            sessionObject.updatedAt = object.updated_at;
          } else {
            this.sessionObjects.push({
              alias: alias,
              updatedAt: object.updated_at
            });
            this.sessionObjects.sort((a, b) => a.alias.localeCompare(b.alias));
          }
        }

        this.loadingSessionObjects.delete(alias);
        this.notify(alias, value);
        this.notifyListeners();
      }, error => {
        console.error(`Failed to load session object ${alias}: ` + error.message);
        this.loadingSessionObjects.delete(alias);
        this.notifyListeners();
      });
  }

  autoRefreshSessionEvents() {
    if (!this.mounted) return;

    this.autoRefreshTimer = setTimeout(() => {
      if (!this.mounted) return;

      if (this.sessionSync) {
          if (this.sessionSyncCounter > 180) {
              this.enableSessionSync(false);
          } else {
              this.sessionSyncCounter++;
          }
      }

      // Poll through "open" AND the transitional statuses ("closing" / "deleting") so the
      // terminal lifecycle event (session_closed / session_deleted) the backend emits later is
      // actually observed -- otherwise the badge/actions get stuck at "closing"/"deleting" until
      // a manual refresh. Stop at "closed": it's a steady state for this client (nothing further
      // changes without a local user action), so there's no live event left to wait for. A
      // delete performed by *another* client against an already-"closed" session here won't be
      // seen live -- a deliberate trade to avoid polling every closed session forever.
      if (this.sessionSync && this.session && (isSessionOpen(this.session) || isSessionTransitioning(this.session))) {
          this.loadRecentSessionEvents();
      }

      this.autoRefreshSessionEvents();
    }, 1000);
  }

  enableSessionSync(enable) {
    this.sessionSync = enable;
    this.sessionSyncCounter = 0;

    if (enable && this.sessionEventCreatedAt === null) {
      // First activation for this session — anchor the cursor at "now"
      // so we don't replay the entire session history on the next poll.
      // `sessionEventCreatedAt` is nulled in `clearSessionState` on
      // session change, so each new session re-anchors here.
      //
      // Subsequent OFF -> ON toggles intentionally DO NOT reset the
      // cursor: the next polling cycle picks up every session event
      // posted while sync was paused — session_object_modified,
      // session_object_deleted, AND session_thread_item_posted — and
      // dispatches them to listeners. That's how a thread widget
      // re-tails items posted by other actors during the sync gap.
      this.sessionEventCreatedAt = new Date().toISOString();
    }

    this.notifyListeners();
  }

  loadRecentSessionEvents() {
    let createdSince = this.sessionEventCreatedAt;
    this.loadSessionEvents(createdSince, true);
  }

  loadSessionEvents(createdSince, ascending) {
    const body = {
      "engine": "workflow_blobhub",
      "command": "list_session_events",
      "session_id": this.sessionId,
      "include_session": true,
      "ascending": ascending
    };

    if (createdSince) {
      body["created_since"] = createdSince;
    }

    const requestedSessionId = this.sessionId;

    this.app.api.revisionsIdDataQueryPost(this.revision.id, body)
      .then(response => {
        if (this.sessionId !== requestedSessionId) return;

        const session = response.data["session"];
        const sessionEvents = response.data["session_events"];

        this.session = session;
        this.sessionEvents = sessionEvents;

        if (sessionEvents.length > 0) {
            this.sessionEventCreatedAt = sessionEvents[sessionEvents.length - 1]["created_at"];
        }

        this.refreshSessionObjects(sessionEvents);
      }, error => {
        console.error("Failed to load session events: " + error.message);
      });
  }

  refreshSessionObjects(sessionEvents) {
    // Lifecycle events take precedence. session_deleted is terminal -- the session and
    // everything in it is gone, so short-circuit the rest of this batch (no point reconciling
    // objects/threads/graphs for a session that no longer exists) and defer to the dedicated
    // handler. The other three lifecycle events just update the current session's status.
    let statusChanged = false;
    for (const sessionEvent of sessionEvents) {
      const type = sessionEvent["type"];
      if ("session_deleted" === type) {
        this.handleSessionDeleted();
        return;
      }
      const status = SESSION_LIFECYCLE_EVENT_STATUS[type];
      if (status && this.session) {
        this.updateCurrentSession({ ...this.session, status });
        statusChanged = true;
      }
    }
    if (statusChanged) {
      this.notifyListeners();
    }

    const aliasesToRefresh = new Set();
    let hasDeletions = false;

    for (const sessionEvent of sessionEvents) {
      if ("session_object_modified" === sessionEvent["type"]) {
        aliasesToRefresh.add(sessionEvent["session_object"]["alias"]);
      } else if ("session_object_deleted" === sessionEvent["type"]) {
        const alias = sessionEvent["session_object"]["alias"];
        
        if (this.sessionObjects) {
          const initialLength = this.sessionObjects.length;
          this.sessionObjects = this.sessionObjects.filter(obj => obj.alias !== alias);
          if (this.sessionObjects.length !== initialLength) hasDeletions = true;
        }
        
        if (alias in this.sessionData) {
            delete this.sessionData[alias];
            hasDeletions = true;
        }
        
        // If deleted, don't refresh even if it was modified in the same batch (unlikely but safe)
        aliasesToRefresh.delete(alias);
      }
    }

    for (const alias of aliasesToRefresh) {
      this.loadSessionObject(alias);
    }

    if (hasDeletions) {
      this.notifyListeners();
    }

    // Dispatch thread events. The backend emits payload {session_object: {alias, item_id}}
    // for all three session-object event types (modified, deleted, thread_item_posted);
    // ExecutionEventsImpl.post_session_event merges that into the event row, so the
    // alias/item_id live under sessionEvent.session_object. We continue to pass a flat
    // {alias, item_id} shape to widget listeners so widgets/thread/index.js doesn't change.
    for (const sessionEvent of sessionEvents) {
      if ("session_thread_item_posted" === sessionEvent["type"]) {
        const so = sessionEvent["session_object"] || {};
        if (so.alias) {
          this.notifyThreadEventListeners(so.alias, { alias: so.alias, item_id: so.item_id });
        }
      } else if ("session_graph_changed" === sessionEvent["type"]) {
        const so = sessionEvent["session_object"] || {};
        if (so.alias) {
          this.notifyGraphEventListeners(so.alias, { alias: so.alias, changes: so.changes });
        }
      }
    }
  }

  createNewSession(session) {
    this.updateProgress(true, "Creating a new session...", false, null);

    const body = {
      engine: "workflow_blobhub",
      command: "create_session"
    };

    if (session) {
      if (session.alias && session.alias.trim()) {
        body["alias"] = session.alias.trim();
      }
      if (session.description && session.description.trim()) {
        body["description"] = session.description.trim();
      }
      if (session.tracing) {
        body["tracing"] = session.tracing;
      }
    }

    return this.app.api.revisionsIdDataCommandPost(this.revision.id, body)
      .then(response => {
        this.updateProgress(false, null, false, null);
        const newSession = response.data["session"];
        this.sessions = [newSession].concat(this.sessions || []);
        if (this.onUpdate) this.onUpdate();
        this.notifyListeners();
        return newSession;
      }, error => {
        this.updateProgress(false, null, true, "Failed to create new session: " + error.message);
      });
  }

  /**
   * Update
   *
   * Issues update_session for the current session and resolves the { ok, aliasInUse, message }
   * shape CreateSessionModal's Promise contract expects (see debugger/session/index.js#updateSession,
   * which this mirrors for the playground's Edit Session menu item). The alias is the one field
   * omitted when blank: the server grammar rejects "" and a blank field means "leave the alias
   * unchanged", not "clear it" -- description and tracing are always sent, since edit mode makes
   * every field editable.
   *
   * On success, the returned session is synced into both `this.session` and its matching entry in
   * `this.sessions` via updateCurrentSession -- the same session-sync path the lifecycle poller
   * uses -- so the playground selector and details rail reflect the change immediately rather than
   * showing the old alias until an unrelated update happens to touch it.
   *
   * Resolves rather than rejects: a 400 alias_in_use is about the value typed into the modal, so the
   * modal owns the message and this must not raise the shell-level error banner. Every other failure
   * (e.g. 409 session_not_open) resolves with the backend's own explanation for the modal to show as
   * a general error.
   */
  updateSession(fields) {
    this.updateProgress(true, "Updating session...", false, null);

    const body = {
      engine: "workflow_blobhub",
      command: "update_session",
      session_id: this.sessionId,
      description: (fields.description || "").trim(),
      tracing: fields.tracing
    };
    const alias = (fields.alias || "").trim();
    if (alias) {
      body["alias"] = alias;
    }

    return this.app.api.revisionsIdDataCommandPost(this.revision.id, body)
      .then(response => {
        this.updateProgress(false, null, false, null);
        const session = response.data["session"];
        this.updateCurrentSession(session);
        this.notifyListeners();
        return { ok: true };
      }, error => {
        this.updateProgress(false, null, false, null);
        if (isUpdateSessionAliasInUse(error)) {
          return { ok: false, aliasInUse: true };
        }
        return { ok: false, message: updateSessionErrorMessage(error) };
      });
  }

  /**
   * Session lifecycle
   *
   * The close/reopen/delete commands themselves are NOT issued from here: the only surface that
   * owns those actions is the debugger session view (debugger/session/index.js), which has no
   * SessionManager instance and dispatches them locally. What this manager does own is the
   * *reaction* to lifecycle changes observed on the session-events poller.
   */

  /**
   * Sets the current session and keeps the matching entry in `this.sessions[]` in sync, so
   * badges rendered from the list (debugger list, playground selector) reflect the change
   * immediately instead of showing stale status until an independent reload. Driven by the
   * session-events poller's lifecycle handling in refreshSessionObjects, which only knows the
   * new status and so merges it onto the current session before calling this.
   */
  updateCurrentSession(session) {
    this.session = session;
    if (this.sessions) {
      const index = this.sessions.findIndex(s => s["id"] === session["id"]);
      if (index !== -1) {
        this.sessions[index] = session;
      }
    }
  }

  /**
   * session_deleted: the session no longer exists server-side. Unselect it locally and refresh
   * the list so it disappears from the selector/list without a manual reload.
   *
   * The notification carries the event type because clearing our own session is not enough:
   * PlaygroundShell holds an independent copy of the selection (state.sessionId) and the URL still
   * names the deleted session. A generic "something changed" tick gives the shell no way to tell
   * this apart, so it would keep building URLs for a session that is gone.
   */
  handleSessionDeleted() {
    this.clearSessionState(null);
    this.notifyListeners({ type: "session_deleted" });
    this.loadSessions();
  }

  uploadSessionObject(alias, value) {
    this.updateProgress(true, "Updating session object...", false, null);
    
    const body = {
      engine: "workflow_blobhub",
      command: "upload_session_object",
      session_id: this.sessionId,
      alias: alias,
      value: value
    };

    return this.app.api.revisionsIdDataCommandPost(this.revision.id, body)
      .then(response => {
        this.updateProgress(false, null, false, null);
        this.sessionData[alias] = value;
        
        // Update session objects list
        if (this.sessionObjects) {
          const existingIndex = this.sessionObjects.findIndex(obj => obj.alias === alias);
          if (existingIndex !== -1) {
            // Update existing
            this.sessionObjects[existingIndex].updatedAt = new Date().toISOString();
          } else {
            // Add new
            this.sessionObjects.push({
              alias: alias,
              updatedAt: new Date().toISOString()
            });
            // Sort by alias
            this.sessionObjects.sort((a, b) => a.alias.localeCompare(b.alias));
          }
        }

        this.notify(alias, value);
        this.notifyListeners();
      }, error => {
        this.updateProgress(false, null, true, "Failed to update session object: " + error.message);
      });
  }

  deleteSessionObject(alias) {
    this.updateProgress(true, "Deleting session object...", false, null);

    const body = {
      engine: "workflow_blobhub",
      command: "delete_session_object",
      session_id: this.sessionId,
      alias: alias
    };

    return this.app.api.revisionsIdDataCommandPost(this.revision.id, body)
      .then(response => {
        this.updateProgress(false, null, false, null);
        
        // Remove from session objects list
        if (this.sessionObjects) {
          this.sessionObjects = this.sessionObjects.filter(obj => obj.alias !== alias);
        }
        
        // Remove data
        delete this.sessionData[alias];
        
        // Notify listeners
        this.notifyListeners();
      }, error => {
        this.updateProgress(false, null, true, "Failed to delete session object: " + error.message);
      });
  }
}
